// Thin fetch wrapper for the MOS API. Same-origin by default (Vite proxy in
// dev, Caddy in prod), so the session cookie flows without CORS.

const BASE = `${import.meta.env.VITE_API_URL ?? ""}/api/v1`;
const CSRF_COOKIE = "mos_csrf";

/** How long one request may take before it counts as unreachable. */
export const REQUEST_TIMEOUT_MS = 30_000;

export class ApiError extends Error {
	readonly status: number;
	readonly code: string;

	constructor(status: number, code: string, message: string) {
		super(message);
		this.name = "ApiError";
		this.status = status;
		this.code = code;
	}
}

type RequestOptions = {
	method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
	body?: unknown;
	signal?: AbortSignal;
};

function readCookie(name: string): string | null {
	const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
	return match ? decodeURIComponent(match[1]) : null;
}

export async function api<T>(path: string, opts: RequestOptions = {}): Promise<T> {
	const method = opts.method ?? "GET";
	const headers: Record<string, string> = { Accept: "application/json" };
	if (opts.body !== undefined) headers["Content-Type"] = "application/json";
	if (method !== "GET") {
		// The API checks this on every write; the cookie is set at login.
		const token = readCookie(CSRF_COOKIE);
		if (token) headers["X-CSRF-Token"] = token;
	}

	// A request that never answers (a stalled connection, a proxy mid-restart)
	// must not hang the caller: the sync loop shows "Syncing" until it settles.
	// After the timeout it counts as unreachable, like being offline.
	const controller = new AbortController();
	const timer = setTimeout(
		() => controller.abort(new DOMException("Request timed out", "TimeoutError")),
		REQUEST_TIMEOUT_MS,
	);
	const onCallerAbort = () => controller.abort(opts.signal?.reason);
	if (opts.signal?.aborted) onCallerAbort();
	else opts.signal?.addEventListener("abort", onCallerAbort, { once: true });

	let res: Response;
	try {
		res = await fetch(`${BASE}${path}`, {
			method,
			headers,
			credentials: "include",
			body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
			signal: controller.signal,
		});
	} catch (err) {
		// The caller cancelling is not a failure; a timeout is.
		clearTimeout(timer);
		opts.signal?.removeEventListener("abort", onCallerAbort);
		if (opts.signal?.aborted) throw err;
		throw new ApiError(0, "network", "Cannot reach the server");
	}

	// The timer keeps running while the body is read: a stalled body hangs the caller too.
	const cleanup = () => {
		clearTimeout(timer);
		opts.signal?.removeEventListener("abort", onCallerAbort);
	};
	if (res.status === 204) {
		cleanup();
		return undefined as T;
	}
	const data: unknown = await res.json().catch(() => null);
	cleanup();
	if (!res.ok) {
		const e = (data as { error?: { code?: string; message?: string } } | null)?.error;
		throw new ApiError(res.status, e?.code ?? "http_error", e?.message ?? res.statusText);
	}
	// A proxy or SPA fallback can answer 200 with HTML; never treat that as data.
	if (data === null)
		throw new ApiError(res.status, "bad_response", "The server sent an unexpected reply");
	return data as T;
}

export type Health = { status: string; database: string; version: string; time: string };
export type Me = { id: string; email: string; display_name: string; timezone: string };

export type LoginInput = { email: string; password: string };

export const apiClient = {
	health: (signal?: AbortSignal) => api<Health>("/health", { signal }),
	me: (signal?: AbortSignal) => api<Me>("/auth/me", { signal }),
	login: (input: LoginInput) => api<Me>("/auth/login", { method: "POST", body: input }),
	logout: () => api<void>("/auth/logout", { method: "POST" }),
};
